import { Fragment, useId, useState } from 'react';
import type { ReactNode } from 'react';

import type { CardFlag, CaseDetailDto, LegalCitation } from '../../api/types';
import { dayWord, fmtDate, fmtDateTime } from '../../lib/format';
import { categoryLabel } from '../../lib/categories';
import { HELP, caveatHelp } from '../../lib/help';
import { HelpTip } from '../HelpTip';
import { sourceLabel, stageLabel } from '../../lib/stages';

interface Row {
    key: string;
    value: ReactNode;
    tone?: 'red' | 'green';
    /** Подсказка «?» к названию строки. */
    help?: string;
}

/** Полное название акта: реквизиты и название («Федеральный закон от 21.11.2011 № 323-ФЗ «Об основах…»»). */
function actName(citation: LegalCitation): string {
    const title = citation.document_title ?? '';
    return citation.attributes_row ? `${citation.attributes_row} «${title}»` : title;
}

/**
 * Норма — одна строка: акт, статья (часть, пункт), редакция, оговорка. По щелчку или Enter/пробелу раскрывается
 * дословная цитата, на которую опирается разбор; без цитаты (разбор n8n) строка не раскрывается.
 */
function NormLine({ citation, id, open, onToggle }: { citation: LegalCitation; id: string; open: boolean; onToggle: () => void }) {
    const head = (
        <span className="norm-head">
            <span className="norm-act">{actName(citation)}</span>
            {citation.article_label ? <span className="norm-article">{citation.article_label}</span> : null}
            {citation.edition_date ? <span className="norm-edition">{'ред. от ' + fmtDate(citation.edition_date)}</span> : null}
            {citation.caveat ? <span className="legal-caveat">{citation.caveat}</span> : null}
        </span>
    );
    const help = citation.caveat ? caveatHelp(citation.caveat) : null;
    return (
        <li className={open ? 'legal-norm norm-open' : 'legal-norm'}>
            <div className="norm-row">
                {citation.excerpt ? (
                    <button type="button" className="norm-toggle" aria-expanded={open} aria-controls={id} onClick={onToggle}>
                        <span className="norm-chev" aria-hidden="true">
                            ▸
                        </span>
                        {head}
                    </button>
                ) : (
                    <div className="norm-toggle norm-static">{head}</div>
                )}
                {help ? <HelpTip text={help} label="оговорка к норме" align="end" /> : null}
            </div>
            {citation.excerpt ? (
                <div className={open ? 'drawer open' : 'drawer'} id={id} aria-hidden={!open}>
                    <div className="drawer-inner">
                        <blockquote className="legal-quote norm-quote">{citation.excerpt}</blockquote>
                    </div>
                </div>
            ) : null}
        </li>
    );
}

/**
 * Нормы правового основания (доработка шага 11): все свёрнуты, главные — первыми, остальные — под «ещё N норм»; главные
 * не выбраны (разбор n8n) — все нормы списком. «Развернуть все» раскрывает и цитаты, и «ещё N норм».
 */
function LegalNorms({ citations }: { citations: LegalCitation[] }) {
    const idBase = useId();
    const [open, setOpen] = useState<ReadonlySet<number>>(new Set());
    const [restOpen, setRestOpen] = useState(false);
    const indexed = citations.map((citation, index) => ({ citation, index }));
    const main = indexed.filter((x) => x.citation.main === true);
    const grouped = main.length > 0;
    const rest = grouped ? indexed.filter((x) => x.citation.main !== true) : indexed;
    const expandable = indexed.filter((x) => Boolean(x.citation.excerpt)).map((x) => x.index);
    const allOpen = expandable.length > 0 && expandable.every((i) => open.has(i)) && (!grouped || rest.length === 0 || restOpen);

    const toggle = (index: number) =>
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    const toggleAll = () => {
        if (allOpen) {
            setOpen(new Set());
            setRestOpen(false);
        } else {
            setOpen(new Set(expandable));
            setRestOpen(true);
        }
    };
    const line = (x: { citation: LegalCitation; index: number }) => (
        <NormLine key={'norm-' + x.index} citation={x.citation} id={idBase + '-' + x.index} open={open.has(x.index)} onToggle={() => toggle(x.index)} />
    );

    return (
        <>
            <div className="legal-norms-bar">
                {grouped ? (
                    <span className="legal-main-head">
                        Главные нормы <HelpTip text={HELP.mainNorms} label="главные нормы" />
                    </span>
                ) : (
                    <span />
                )}
                {expandable.length > 0 ? (
                    <button type="button" className="link-btn" onClick={toggleAll}>
                        {allOpen ? 'Свернуть все' : 'Развернуть все'}
                    </button>
                ) : null}
            </div>
            {grouped ? (
                <>
                    <ul className="legal-norms">{main.map(line)}</ul>
                    {rest.length > 0 ? (
                        <details className="legal-more" open={restOpen} onToggle={(event) => setRestOpen(event.currentTarget.open)}>
                            <summary>{'ещё ' + rest.length + ' ' + normWord(rest.length)}</summary>
                            <ul className="legal-norms">{rest.map(line)}</ul>
                        </details>
                    ) : null}
                </>
            ) : (
                <ul className="legal-norms">{rest.map(line)}</ul>
            )}
        </>
    );
}

function normWord(n: number): string {
    const d = n % 10;
    const h = n % 100;
    if (d === 1 && h !== 11) return 'норма';
    if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'нормы';
    return 'норм';
}

function disclosureText(value: boolean | string | null): string {
    if (value === true) return 'допустимо';
    if (value === false) return 'не допускается';
    if (typeof value === 'string' && value) return value;
    return 'не определено';
}

function activeFlags(flags: CardFlag[]): CardFlag[] {
    return flags.filter((flag) => flag.active);
}

export interface TabCardProps {
    detail: CaseDetailDto;
    onOpenCase: (caseId: string) => void;
}

/** Вкладка «Карточка дела» (ТЗ §8.5.3): все поля дела и классификации в читаемом виде. */
export function TabCard({ detail, onOpenCase }: TabCardProps) {
    const { case: item, card, departments, executors } = detail;
    const [showOriginal, setShowOriginal] = useState(false);

    const department = departments.find((d) => d.id === item.department_id) ?? null;
    const executor = executors.find((e) => e.id === item.executor_id) ?? null;
    const left = item.days_left ?? 0;
    const closed = item.stage === 'sent' || Boolean(item.closed_at);

    // Число и склонение слова берём из одной величины: `overdue_days` и `days_left` —
    // разные поля контракта, и при расхождении вышло бы «просрочено на 21 дня».
    const overdue = Math.abs(item.overdue_days ?? left);

    const status: Row = closed
        ? {
              key: 'Статус',
              value: 'Закрыто ' + (fmtDate(item.closed_at ?? item.sent_at) || 'дата не указана'),
              tone: 'green',
          }
        : left < 0
          ? {
                key: 'Статус',
                value: 'Просрочено на ' + overdue + ' ' + dayWord(overdue),
                tone: 'red',
            }
          : { key: 'Статус', value: 'В работе · осталось ' + Math.max(left, 0) + ' ' + dayWord(Math.max(left, 0)) };

    const rows: Row[] = [status];

    rows.push({ key: 'Номер дела', value: item.number });
    rows.push({ key: 'Вид обращения', value: card.appeal_type.length > 0 ? card.appeal_type.join(', ') : '—' });
    rows.push({ key: 'Заявитель', value: card.applicant_name ?? item.applicant ?? '—' });
    if (card.applicant_role) rows.push({ key: 'Кем приходится пациенту', value: card.applicant_role });
    rows.push({ key: 'Раскрытие сведений', value: disclosureText(card.disclosure_allowed) });
    rows.push({ key: 'Категория', value: categoryLabel(item.primary_category ?? card.primary_category) || '—' });
    if (card.categories.length > 1) rows.push({ key: 'Все категории', value: card.categories.map(categoryLabel).join(', ') });
    rows.push({ key: 'Приоритет', value: String(item.priority) });
    rows.push({ key: 'Стадия', value: stageLabel(item.stage) || item.stage_label });
    rows.push({
        key: 'Подразделение',
        value: department ? (department.icon ? department.icon + ' ' : '') + department.name : (item.department_name ?? 'не указано'),
    });
    if (department && department.head_name) rows.push({ key: 'Руководитель', value: department.head_name });
    rows.push({
        key: 'Исполнитель',
        value: item.executor_name
            ? item.executor_name + (executor && executor.post && executor.post !== item.executor_name ? ' · ' + executor.post : '')
            : 'не назначен',
    });
    rows.push({ key: 'Ответственный по регламенту', value: card.responsible_dept ?? item.responsible_dept ?? '—' });
    rows.push({ key: 'Источник', value: sourceLabel(item.source) });
    rows.push({ key: 'Поступило', value: fmtDateTime(item.received_at) || '—' });
    rows.push({ key: 'Зарегистрировано', value: fmtDateTime(item.created_at) || '—' });
    rows.push({ key: 'Срок регистрации', value: fmtDate(item.registration_deadline) || '—' });
    rows.push({ key: 'Срок ответа (внутренний)', value: fmtDate(item.response_deadline) || '—', help: HELP.deadlineInternal });
    rows.push({
        key: 'Срок по закону',
        value:
            (fmtDate(item.legal_deadline) || '—') +
            (card.legal_deadline_days ? ' (норматив ' + card.legal_deadline_days + ' ' + dayWord(card.legal_deadline_days) + ')' : ''),
        tone: item.deadline_state === 'late' ? 'red' : undefined,
        help: HELP.deadlineLegal,
    });
    rows.push({ key: 'Тип ответа', value: card.response_type ?? item.response_type ?? '—' });
    rows.push({ key: 'Уверенность квалификации', value: card.confidence ?? item.confidence ?? '—' });

    if (item.extended) {
        rows.push({
            key: 'Продление срока',
            value:
                'на 30 дней · ' +
                (item.extend_reason ?? 'основание не указано') +
                (item.extend_approved === true
                    ? ' · утверждено главным врачом'
                    : item.extend_approved === false
                      ? ' · отклонено'
                      : ' · ждёт решения главного врача'),
            tone: item.extend_approved === true ? 'green' : undefined,
        });
    }
    if (item.escalation_risk) rows.push({ key: 'Риск проверки', value: 'надзор или суд', tone: 'red' });
    if (item.escalated) rows.push({ key: 'Эскалация', value: 'запрошена исполнителем', tone: 'red' });
    if (item.repeat_of) rows.push({ key: 'Повторное обращение', value: 'связано с предыдущим делом' });
    if (item.approved) {
        rows.push({
            key: 'Согласование',
            value: (item.approved_by_name ?? 'Главный врач') + ' · ' + (fmtDateTime(item.approved_at) || 'дата не указана'),
            tone: 'green',
        });
    }
    if (closed) {
        rows.push({
            key: 'Результат рассмотрения',
            value: item.justified ? 'Признано обоснованным' : 'Доводы не подтвердились',
            tone: item.justified ? 'green' : undefined,
        });
    }
    if (item.parse_failed || card.parse_failed) {
        rows.push({ key: 'Квалификация', value: 'Требует ручной проверки юриста', tone: 'red' });
    }
    if (detail.reviews.length > 0) {
        const last = detail.reviews[detail.reviews.length - 1];
        rows.push({
            key: 'Оценка юриста',
            value: (last.verdict === 'ok' ? 'Замечаний нет' : 'Есть замечания') + (last.comment ? ' · ' + last.comment : ''),
        });
    }

    const actions = activeFlags(card.actions);
    const procedural = activeFlags(card.procedural);
    const legal = card.legal_basis;
    const originalText = item.original_text_restored ?? item.original_text;

    return (
        <>
            <div className="kv">
                {rows.map((row, index) => (
                    <Fragment key={row.key + '#' + index}>
                        <span className="k">
                            {row.key}
                            {row.help ? <HelpTip text={row.help} label={row.key.toLowerCase()} /> : null}
                        </span>
                        <span className={row.tone ? 'v ' + row.tone : 'v'}>{row.value}</span>
                    </Fragment>
                ))}
            </div>

            {!item.received_at_provided ? (
                <div className="modal-note">
                    Дата поступления обращения не была указана — сроки рассчитаны от даты обработки.
                </div>
            ) : null}

            {card.essence ? (
                <div className="case-section">
                    <h4>Суть обращения</h4>
                    <p>{card.essence}</p>
                </div>
            ) : null}

            {card.patient_demands ? (
                <div className="case-section">
                    <h4>Требование заявителя</h4>
                    <p>{card.patient_demands}</p>
                </div>
            ) : null}

            {card.requests.length > 0 ? (
                <div className="case-section">
                    <h4>Предметы обращения</h4>
                    <ul className="doc-list">
                        {card.requests.map((request, index) => (
                            <li key={request + '#' + index}>{request}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {actions.length > 0 ? (
                <div className="case-section">
                    <h4>Мероприятия</h4>
                    <ul className="doc-list">
                        {actions.map((flag) => (
                            <li key={flag.key}>{flag.label}</li>
                        ))}
                    </ul>
                    {card.actions_reasoning ? <p>{card.actions_reasoning}</p> : null}
                </div>
            ) : null}

            {procedural.length > 0 || card.procedural_notes.length > 0 ? (
                <div className="case-section">
                    <h4>Процессуальные особенности</h4>
                    <ul className="doc-list">
                        {procedural.map((flag) => (
                            <li key={flag.key}>{flag.label}</li>
                        ))}
                        {card.procedural_notes.map((note, index) => (
                            <li key={'note-' + index}>{note}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {card.assignments.length > 0 ? (
                <div className="case-section">
                    <h4>Поручения</h4>
                    <ul className="doc-list">
                        {card.assignments.map((assignment, index) => (
                            <li key={'assignment-' + index}>
                                {assignment.executor + ' — ' + assignment.action + (assignment.basis ? ' · ' + assignment.basis : '')}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {card.inquiry_questions.length > 0 ? (
                <div className="case-section">
                    <h4>Что запросить внутри организации</h4>
                    <ul className="doc-list">
                        {card.inquiry_questions.map((question, index) => (
                            <li key={'inquiry-' + index}>{question}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {card.clarifications_for_applicant.length > 0 ? (
                <div className="case-section">
                    <h4>Уточнить у заявителя</h4>
                    <ul className="doc-list">
                        {card.clarifications_for_applicant.map((question, index) => (
                            <li key={'clarify-' + index}>{question}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {card.required_documents.length > 0 ? (
                <div className="case-section">
                    <h4>Какие документы нужны</h4>
                    <ul className="doc-list">
                        {card.required_documents.map((doc, index) => (
                            <li key={'doc-' + index}>{doc}</li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {legal && (legal.violated_right || legal.citations.length > 0) ? (
                <div className="case-section">
                    <h4>⚖️ Нормы из базы</h4>
                    <div className="legal-box">
                        {legal.violated_right ? <p className="legal-right">{legal.violated_right}</p> : null}
                        {legal.citations.length > 0 ? <LegalNorms citations={legal.citations} /> : null}
                    </div>
                </div>
            ) : null}

            {card.normative_procedure ? (
                <div className="case-section">
                    <h4>Порядок рассмотрения</h4>
                    <p>{card.normative_procedure}</p>
                </div>
            ) : null}

            {detail.related.length > 0 ? (
                <div className="case-section">
                    <h4>🔁 Связанные обращения</h4>
                    {detail.related.map((related) => (
                        <div
                            key={related.case_id}
                            className="linked-row"
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpenCase(related.case_id)}
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter' && event.key !== ' ') return;
                                event.preventDefault();
                                onOpenCase(related.case_id);
                            }}
                        >
                            <span className="ln-date">{fmtDate(related.created_at)}</span>
                            <span className="ln-title">{related.number + ' · ' + related.title}</span>
                            <span className={'badge ' + (related.stage === 'sent' ? 'closed' : 'open')}>
                                {related.stage === 'sent' ? 'закрыто' : 'в работе'}
                            </span>
                        </div>
                    ))}
                </div>
            ) : null}

            <div className="case-section">
                <h4>Исходный текст обращения</h4>
                <div className="draft-bar">
                    <button type="button" className="act-btn" onClick={() => setShowOriginal((prev) => !prev)}>
                        {showOriginal ? 'Скрыть исходный текст' : 'Показать исходный текст'}
                    </button>
                    <span className="draft-note">
                        Персональные данные восстановлены по карте обезличивания — это внутренняя система клиники.
                    </span>
                </div>
                {showOriginal ? (
                    <div className="thread">
                        <div className="msg bot">{originalText ?? 'Текст обращения не сохранён.'}</div>
                    </div>
                ) : null}
            </div>
        </>
    );
}

export default TabCard;
